<template>
  <button
    type="button"
    class="favourite-tour-button u-clickable"
    :class="{ '_is-favourite': isFavorite }"
    :disabled="pending"
    @click="toggleFavorite"
  >
    <slot>
      <span class="favourite-tour-button__container">
        <span class="favourite-tour-button__text font-h2">{{
          $t(`common.save${isFavorite ? "d" : ""}`)
        }}</span>
        <span class="favourite-tour-button__icon u-flex-shrink-0">
          <svg-inline tag="span" class="u-stroke-deep-current">
            <icon icon="heart" />
          </svg-inline>
        </span>
      </span>
    </slot>
  </button>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { ytEvents } from "@/plugins/analytics";
import { axiosUser } from "@/services/scopes/axios-user";
import SvgInline from "@/components/ui-kit/svg-inline/index.vue";
import Icon from "@/components/icons/index.vue";

export default {
  components: {
    SvgInline,
    Icon,
  },
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      eventName: "favorite-tour",
      pending: false,
    };
  },
  computed: {
    ...mapState("user", ["favorites"]),
    ...mapState("user", ["isLoggedIn"]),
    isFavorite() {
      return this.favorites?.map(String).includes(this.id.toString());
    },
  },
  mounted() {
    fakeWindow.addEventListener("auth.close", this.onAuthClose);
    fakeWindow.addEventListener("auth.success", this.onAuthSuccess);
  },
  beforeDestroy() {
    fakeWindow.removeEventListener("auth.close", this.onAuthClose);
    fakeWindow.removeEventListener("auth.success", this.onAuthSuccess);
  },
  methods: {
    ...mapActions("user", ["setFavorites"]),
    toggleFavorite() {
      if (this.pending) {
        return;
      }

      const { id, isFavorite } = this;

      ytEvents.track("Tour detail", "Click " + (isFavorite ? "Remove" : "Add") + " to favorites button", id);

      if (this.isLoggedIn) {
        this.pending = true;
        return axiosUser[isFavorite ? "favouriteRemove" : "favouriteAdd"]({
          id,
        })
          .finally(() => {
            this.pending = false;
          })
          .then(
            ({
              data: {
                data: { favourites },
              },
            }) => {
              this.setFavorites(favourites);
            }
          )
          .catch(console.log.bind(console));
      }

      fakeWindow.showVueAuthModal({ "emit-event": this.eventName + "-" + id });
    },
    onAuthClose({ detail }) {
      const event = this.eventName + "-" + this.id;
      if (detail === event) {
        this.pending = false;
      }
    },
    onAuthSuccess({ detail }) {
      this.isLoggedIn = true;
      const event = this.eventName + "-" + this.id;
      if (detail === event) {
        setTimeout(() => {
          this.toggleFavorite();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$b: ".favourite-tour-button";

#{$b} {
  @include mixin.button-reset();

  &:disabled {
    @include mixin.decor();
  }

  &__container {
    display: flex;
    align-items: center;
    padding: 12px;
    border-radius: 50%;
    background: var.$white;
    color: var.$black;
    box-shadow: 1px 4px 20px rgba(var.$secondary, 0.35);
    transition: var.$transition-default;

    @include mixin.size-md-min {
      padding: 10px 14px;
      border-radius: 50px;
    }

    &:hover {
      background: var.$gray-pale;
    }

    #{$b}._is-favourite & {
      color: var.$green-dark;
    }
  }

  &__text,
  &__icon {
    transition: var.$transition-fast;

    #{$b}:disabled & {
      opacity: 0.5;
    }
  }

  &__text {
    margin-right: 6px;
    display: none;

    @include mixin.size-md-min {
      display: block;
    }
  }

  &__icon {
    width: 20px;
  }
}
</style>
